/**
 * Client-side helpers for the `social_link_verifications` array that rides
 * rec league payloads. Pure module (no model imports): the profile header,
 * the settings form, and their tests all use it. Mirrored in
 * `mobile/lib/util/socialLinkVerification.ts` — keep the two in sync.
 */
import type { SocialLinkKey } from "./recLeagueSocialLinks.ts";
import { getWebsiteLinkLabel } from "./recLeagueSocialLinks.ts";
import { socialLinksEqual } from "./socialLinkParser.ts";

export type VerifiablePlatform =
  | "instagram"
  | "substack"
  | "website"
  | "tiktok"
  | "youtube"
  | "threads"
  | "x"
  | "letterboxd"
  | "goodreads";

export type SocialLinkVerificationStatus =
  | "pending"
  | "verified"
  | "failed"
  | "revoked";

/** The client shape (see SocialLinkVerification.toClientJSON / BACKEND_ONLY_FIELDS). */
export interface ClientSocialLinkVerification {
  id: number;
  platform: VerifiablePlatform;
  url: string;
  status: SocialLinkVerificationStatus;
  attempts: number;
  submitted_at: string | Date;
  verified_at: string | Date | null;
  failed_at: string | Date | null;
  needs_manual_review: boolean;
}

export interface HasSocialLinkVerifications {
  social_link_verifications?: readonly ClientSocialLinkVerification[] | null;
}

/** The social-link column each verifiable platform lives on (Spotify has no bio or link field, so it can't be verified). */
export const SOCIAL_KEY_BY_PLATFORM: Record<VerifiablePlatform, SocialLinkKey> = {
  instagram: "instagram_url",
  substack: "substack_url",
  website: "website_url",
  tiktok: "tiktok_url",
  youtube: "youtube_url",
  threads: "threads_url",
  x: "x_url",
  letterboxd: "letterboxd_url",
  goodreads: "goodreads_url",
};

/** The verifiable platform each social-link column maps to. */
export const VERIFIABLE_PLATFORM_BY_KEY: Partial<
  Record<SocialLinkKey, VerifiablePlatform>
> = Object.fromEntries(
  Object.entries(SOCIAL_KEY_BY_PLATFORM).map(([platform, key]) => [key, platform]),
);

/** Loose link equality: two spellings of the same link are equal. */
export { socialLinksEqual };

/**
 * The live verification for a specific link, if any. Revoked rows never ride
 * the payload, but are filtered defensively.
 */
export function getSocialLinkVerification(
  recLeague: HasSocialLinkVerifications,
  platform: VerifiablePlatform,
  url: string | null | undefined,
): ClientSocialLinkVerification | null {
  if (!url) return null;
  return (
    recLeague.social_link_verifications?.find(
      (row) =>
        row.status !== "revoked" &&
        row.platform === platform &&
        socialLinksEqual(row.url, url),
    ) ?? null
  );
}

export function isSocialLinkVerified(
  recLeague: HasSocialLinkVerifications,
  platform: VerifiablePlatform,
  url: string | null | undefined,
): boolean {
  return getSocialLinkVerification(recLeague, platform, url)?.status === "verified";
}

/**
 * Copy per platform for the settings-form verification module and its stepped
 * dialog. `link` is the thing being verified, in the second person ("your
 * Instagram link") or, for a website, its bare hostname. Mirrors the mobile
 * `verificationCopy` — keep the two in sync.
 */
export interface VerificationCopy {
  name: string;
  /** Module, verified: "Instagram verified". */
  verifiedTitle: string;
  /** Module, initial: "Your Instagram link isn’t verified yet." */
  unverifiedNotice: string;
  /** Dialog, step 1 subtitle: where the link goes and why. */
  copySubtitle: string;
  /** Dialog, step 2 title: "Add your Rec League URL to your Instagram bio". */
  pasteTitle: string;
  /**
   * Dialog, step 2 body: what it proves, and to press Done afterwards. Split
   * where it names the curator's Rec League URL, so the dialog can show the
   * URL itself (in parens, with a copy button beside it) right there:
   * `{beforeUrl} (recleague.com/you [copy]){afterUrl}`. With a walkthrough,
   * the drawing already shows the URL, so the body runs straight on:
   * `{beforeUrl}{afterUrl}` (see `urlInBody`).
   */
  pasteBody: { beforeUrl: string; afterUrl: string };
  /**
   * Step 2, its own paragraph above the body: why we ask ("This will allow
   * us to confirm that you own…"), on the platforms whose body is the taps.
   */
  pasteLead: string | null;
  /** False when the walkthrough shows the URL and the body leaves it out. */
  urlInBody: boolean;
  /** Dialog, step 2, under the body: a fallback place, where there is one. */
  pasteTip: string | null;
  /**
   * The drawn walkthrough step 2 shows above the instructions, for the
   * platforms most curators verify (null: text only).
   */
  walkthrough: VerificationWalkthroughKind | null;
  /**
   * Where step 2's "Open" button goes when it isn't the saved profile link:
   * the publication's settings page, for the Substack menu ask.
   */
  settingsUrl: string | null;
  /** Where we look: "@handle’s bio". */
  place: string;
  /** Dialog, step 3: "…we’ll verify your Instagram link." */
  link: string;
  /** Claimed elsewhere: "the Instagram account @handle". */
  account: string;
}

/**
 * The drawings: three phone flows (each app's edit-profile screens), and
 * Substack's publication settings in a browser, for curators on a computer.
 */
export type VerificationWalkthroughKind =
  | "instagram"
  | "tiktok"
  | "substack_profile"
  | "substack_menu";

/** The username in a stored profile link, for copy ("@handle’s bio"); null for links without one. */
const HANDLE_PATTERNS: Partial<Record<VerifiablePlatform, RegExp>> = {
  instagram: /instagram\.com\/([A-Za-z0-9._]+)/i,
  tiktok: /tiktok\.com\/@([A-Za-z0-9._]+)/i,
  youtube: /youtube\.com\/@([A-Za-z0-9._-]+)/i,
  threads: /threads\.(?:net|com)\/@([A-Za-z0-9._]+)/i,
  x: /(?:x|twitter)\.com\/([A-Za-z0-9_]+)/i,
  letterboxd: /letterboxd\.com\/([A-Za-z0-9_]+)/i,
  // goodreads.com/<vanity>; the /user/show/<id> form carries no handle.
  goodreads: /goodreads\.com\/(?!user\/|author\/)([A-Za-z0-9._-]+)/i,
};

function profileHandle(platform: VerifiablePlatform, url: string): string | null {
  return url.match(HANDLE_PATTERNS[platform] ?? /$^/)?.[1] ?? null;
}

/**
 * Platforms where the Rec League URL goes in the bio (Instagram, TikTok,
 * Threads): "Add your Rec League URL to your TikTok bio".
 */
function bioPlatformCopy(name: string, handle: string | null): VerificationCopy {
  return {
    name,
    verifiedTitle: `${name} verified`,
    unverifiedNotice: `Your ${name} link isn’t verified yet.`,
    copySubtitle: `You’ll paste this on your ${name} profile to verify that you own it.`,
    pasteTitle: `Add your Rec League URL to your ${name} bio`,
    pasteBody: {
      beforeUrl: `${ownershipLine(name, handle)} After your link`,
      afterUrl: ` is saved to your ${name} bio, press “Done”`,
    },
    pasteLead: null,
    urlInBody: true,
    pasteTip: null,
    walkthrough: null,
    settingsUrl: null,
    place: handle ? `@${handle}’s bio` : `your ${name} bio`,
    link: `your ${name} link`,
    account: handle ? `the ${name} account @${handle}` : `that ${name} account`,
  };
}

/** Why we ask: "This will allow us to confirm that you own the @handle Instagram account." */
function ownershipLine(name: string, handle: string | null): string {
  return `This will allow us to confirm that you own ${
    handle ? `the @${handle}` : "your"
  } ${name} account.`;
}

/**
 * Instagram, where most curators verify: the exact taps (the walkthrough
 * draws them), since the most common failure is a Done pressed with nothing
 * pasted.
 */
function instagramCopy(handle: string | null): VerificationCopy {
  return {
    ...bioPlatformCopy("Instagram", handle),
    pasteBody: {
      beforeUrl: `In Instagram, tap Edit profile, then Links, then Add link, and paste your Rec League URL`,
      afterUrl: ". Then come back here and press “Done”",
    },
    pasteLead: ownershipLine("Instagram", handle),
    urlInBody: false,
    walkthrough: "instagram",
  };
}

/** TikTok: the link goes in the profile's Website field (the walkthrough draws it). */
function tiktokCopy(handle: string | null): VerificationCopy {
  return {
    ...bioPlatformCopy("TikTok", handle),
    pasteBody: {
      beforeUrl: `In TikTok, tap Edit profile, then Website, and paste your Rec League URL`,
      afterUrl: ". Then come back here and press “Done”",
    },
    pasteLead: ownershipLine("TikTok", handle),
    urlInBody: false,
    walkthrough: "tiktok",
  };
}

/**
 * The host of a Substack publication link (`name.substack.com`,
 * `open.substack.com/pub/name`, or a custom domain like `mydomain.com`, which
 * serves the same settings pages); null for a profile (`substack.com/@name`).
 * A regex, not `URL`, so the mirrored copy behaves the same in React Native.
 */
function substackPublicationHost(url: string): string | null {
  const pub = url.match(/^(?:https?:\/\/)?open\.substack\.com\/pub\/([A-Za-z0-9-]+)/i);
  if (pub) return `${pub[1].toLowerCase()}.substack.com`;
  const host = url.match(/^(?:https?:\/\/)?([A-Za-z0-9.-]+)(?:[:/?#]|$)/)?.[1]?.toLowerCase();
  if (!host || !host.includes(".")) return null;
  if (["substack.com", "www.substack.com", "open.substack.com"].includes(host)) return null;
  return host;
}

/**
 * Substack. In the app (and in a phone browser) the ask is the profile's
 * social links, which the Substack app can edit. On a computer, a curator
 * whose link is a publication gets the menu ask instead: the menu sits on
 * every page of the site, where readers click, but Substack only lets you
 * edit it in a desktop browser. The checker reads both, so either verifies.
 */
function substackCopy(url: string, onComputer: boolean): VerificationCopy {
  const handle = url.match(/substack\.com\/@([A-Za-z0-9_.-]+)/i)?.[1];
  const host = substackPublicationHost(url);
  // The publication's own host names it (an open.substack.com/pub/ link
  // reads as name.substack.com); the menu ask is for a computer only.
  const label = (host ?? getWebsiteLinkLabel(url)).replace(/^www\./, "");
  const publicationHost = onComputer ? host : null;
  const shared = {
    pasteLead: `This will allow us to confirm that you own the ${
      handle ? `@${handle}` : label
    } Substack account.`,
    name: "Substack",
    verifiedTitle: "Substack verified",
    unverifiedNotice: "Your Substack link isn’t verified yet.",
    urlInBody: false,
    pasteTip: null,
    place: handle ? `@${handle}’s Substack` : label,
    link: "your Substack link",
    account: handle ? `the Substack account @${handle}` : `the Substack ${label}`,
  };
  if (publicationHost) {
    return {
      ...shared,
      copySubtitle:
        "You’ll add this to your Substack’s menu to verify that you own it.",
      pasteTitle: "Add your Rec League URL to your Substack’s menu",
      pasteBody: {
        beforeUrl: `In your Substack settings, under Navigation bar, click Add item and paste your Rec League URL as the link`,
        afterUrl: ". Then come back here and press “Done”",
      },
      walkthrough: "substack_menu",
      settingsUrl: `https://${publicationHost}/publish/settings#website`,
    };
  }
  return {
    ...shared,
    copySubtitle:
      "You’ll paste this on your Substack profile to verify that you own it.",
    pasteTitle: "Add your Rec League URL to your Substack profile",
    pasteBody: {
      beforeUrl: `In the Substack app, tap Edit profile, then Social Links, then Website, and paste your Rec League URL`,
      afterUrl: ". Then come back here and press “Done”",
    },
    walkthrough: "substack_profile",
    settingsUrl: null,
  };
}

/**
 * Platforms with a website field on the profile (X, Letterboxd, Goodreads):
 * "Add your Rec League URL as the website on your X profile".
 * `fieldHint` says where that field is, in parentheses.
 */
function profileFieldCopy(
  name: string,
  handle: string | null,
  fieldHint: string,
): VerificationCopy {
  return {
    name,
    verifiedTitle: `${name} verified`,
    unverifiedNotice: `Your ${name} link isn’t verified yet.`,
    copySubtitle: `You’ll paste this on your ${name} profile to verify that you own it.`,
    pasteTitle: `Add your Rec League URL to your ${name} profile`,
    pasteBody: {
      beforeUrl: "Add your Rec League URL",
      afterUrl: ` as the website on your ${name} profile (${fieldHint}). This will allow us to confirm that you own ${
        handle ? `the @${handle}` : "your"
      } ${name} account. Once you’ve done that, press “Done”`,
    },
    pasteLead: null,
    urlInBody: true,
    pasteTip: null,
    walkthrough: null,
    settingsUrl: null,
    place: handle ? `@${handle}’s ${name} profile` : `your ${name} profile`,
    link: `your ${name} link`,
    account: handle ? `the ${name} account @${handle}` : `that ${name} account`,
  };
}

/**
 * `onComputer`: the curator is in a desktop browser, where Substack's menu
 * settings work (see `substackCopy`). The app never passes it.
 */
export function verificationCopy(
  platform: VerifiablePlatform,
  url: string,
  options: { onComputer?: boolean } = {},
): VerificationCopy {
  switch (platform) {
    case "instagram":
      return instagramCopy(profileHandle(platform, url));
    case "substack":
      return substackCopy(url, options.onComputer ?? false);
    case "website": {
      const label = getWebsiteLinkLabel(url);
      return {
        name: "website",
        verifiedTitle: `${label} verified`,
        unverifiedNotice: `${label} isn’t verified yet.`,
        copySubtitle:
          "You’ll put this on your website to verify that you own it.",
        pasteTitle: "Add your link to your website",
        pasteBody: {
          beforeUrl: "Add a link to your Rec League URL",
          afterUrl: ` somewhere on ${label}. This will allow us to confirm that you own the website. Once you’ve done that, press “Done”`,
        },
        pasteLead: null,
        urlInBody: true,
        pasteTip: null,
        walkthrough: null,
        settingsUrl: null,
        place: label,
        link: label,
        account: `the website ${label}`,
      };
    }
    case "tiktok":
      return tiktokCopy(profileHandle(platform, url));
    case "threads":
      return bioPlatformCopy("Threads", profileHandle(platform, url));
    case "youtube": {
      const handle = profileHandle(platform, url);
      return {
        name: "YouTube",
        verifiedTitle: "YouTube verified",
        unverifiedNotice: "Your YouTube link isn’t verified yet.",
        copySubtitle:
          "You’ll add this to your YouTube channel to verify that you own it.",
        pasteTitle: "Add your Rec League URL to your channel’s links",
        pasteBody: {
          beforeUrl: "Add your Rec League URL",
          afterUrl: ` to the links on your channel’s About page (in YouTube Studio: Customize channel, then Links). This will allow us to confirm that you own ${
            handle ? `the @${handle}` : "your"
          } YouTube channel. Once you’ve done that, press “Done”`,
        },
        pasteLead: null,
        urlInBody: true,
        pasteTip: null,
        walkthrough: null,
        settingsUrl: null,
        place: handle ? `@${handle}’s About page` : "your channel’s About page",
        link: "your YouTube link",
        account: handle ? `the YouTube channel @${handle}` : "that YouTube channel",
      };
    }
    case "x":
      return profileFieldCopy("X", profileHandle(platform, url), "or in your bio");
    case "letterboxd":
      return profileFieldCopy(
        "Letterboxd",
        profileHandle(platform, url),
        "under “Website” in your profile settings",
      );
    case "goodreads":
      return profileFieldCopy(
        "Goodreads",
        profileHandle(platform, url),
        "under “My Web Site” when you edit your profile",
      );
  }
}

/**
 * Why a check missed (the server's `socialLinkMissReason`), as the server
 * sends it on the verification poll.
 */
export type SocialLinkMissReason =
  | "removed"
  | "homepage_only"
  | "other_link"
  | "link_in_bio_page"
  | "missing";

/**
 * What a failed check saw, then the fix: the dialog's failure step and the
 * failure email both say it this way. `place` is where we looked ("@handle’s
 * bio", "your Instagram bio"); `name` is the platform ("Instagram").
 */
export function verificationMissCopy(
  reason: SocialLinkMissReason,
  place: string,
  name: string,
): { lead: string; action: string } {
  switch (reason) {
    case "homepage_only":
      return {
        lead: `We found recleague.com in ${place}, but not the link to your own page.`,
        action: "Swap it for your full link, then submit again.",
      };
    case "other_link":
      return {
        lead: `We found a Rec League link in ${place}, but it goes to a different page.`,
        action: "Swap it for your own link, then submit again.",
      };
    case "link_in_bio_page":
      return {
        lead: `${capitalize(place)} links to a link-in-bio page (like Linktree), and we can’t see inside those yet.`,
        action: `Add your Rec League link as its own link on ${name}, then submit again.`,
      };
    // A link that was up and came down reads the same as one never added:
    // it isn't there, so add it and try again.
    case "removed":
    case "missing":
      return {
        lead: `We couldn’t find your Rec League link in ${place}.`,
        action: "Add it there (and make sure it’s saved), then submit again.",
      };
  }
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
